<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { useApi } from '../../shared/composables/useApi.js'

const props = defineProps({
  csrfToken: { type: String, required: true },
  redirect:  { type: String, default: '/' },
  email:     { type: String, default: '' },
  initialError:   { type: String, default: '' },
  initialSuccess: { type: String, default: '' },
  // Paid-registration handoff: forwarded to the login API so it can resolve
  // a post-login Plus checkout intent even when the session key was lost.
  plan: { type: String, default: '' },
  next: { type: String, default: '' },
})

const emit = defineEmits(['step'])

const { post } = useApi(props.csrfToken)

const email    = ref(props.email)
const password = ref('')
const remember = ref(false)
const error    = ref(props.initialError)
const success  = ref(props.initialSuccess)
const busy     = ref(false)

const passkeyError    = ref('')
const passkeyBusy     = ref(false)
let conditionalAbort  = null

function b64uToBuf(b64u) {
  const b64 = b64u.replace(/-/g, '+').replace(/_/g, '/')
  const pad = b64.length % 4 ? '='.repeat(4 - (b64.length % 4)) : ''
  const bin = atob(b64 + pad)
  const buf = new ArrayBuffer(bin.length)
  const view = new Uint8Array(buf)
  for (let i = 0; i < bin.length; i++) view[i] = bin.charCodeAt(i)
  return buf
}
function bufToB64u(buf) {
  const bytes = new Uint8Array(buf)
  let s = ''
  for (let i = 0; i < bytes.byteLength; i++) s += String.fromCharCode(bytes[i])
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

async function fetchPasskeyOptions() {
  const r = await fetch('/api/auth/passkey-challenge', { method: 'GET' })
  const d = await r.json().catch(() => ({}))
  if (!r.ok || !d.ok) throw new Error(d.error || 'Could not start passkey sign-in.')
  return d.options
}

function decodeAssertionOptions(opts) {
  return {
    ...opts,
    challenge: b64uToBuf(opts.challenge),
    allowCredentials: (opts.allowCredentials || []).map(c => ({
      ...c, id: b64uToBuf(c.id),
    })),
  }
}

function serializeAssertion(cred) {
  if (typeof cred.toJSON === 'function') return cred.toJSON()
  return {
    id: cred.id,
    rawId: bufToB64u(cred.rawId),
    type: cred.type,
    response: {
      clientDataJSON:    bufToB64u(cred.response.clientDataJSON),
      authenticatorData: bufToB64u(cred.response.authenticatorData),
      signature:         bufToB64u(cred.response.signature),
      userHandle:        cred.response.userHandle ? bufToB64u(cred.response.userHandle) : null,
    },
  }
}

async function submitAssertion(cred) {
  const fd = new FormData()
  fd.append('csrf_token', props.csrfToken)
  fd.append('credential', JSON.stringify(serializeAssertion(cred)))
  const r = await fetch('/api/auth/passkey-login', { method: 'POST', body: fd })
  const d = await r.json().catch(() => ({}))
  if (!r.ok || !d.ok) throw new Error(d.error || 'Passkey sign-in failed.')
  window.location.href = d.redirect || '/'
}

async function startVisibleButton() {
  if (conditionalAbort) { conditionalAbort.abort(); conditionalAbort = null }
  if (passkeyBusy.value) return
  passkeyBusy.value = true
  passkeyError.value = ''
  try {
    const opts = decodeAssertionOptions(await fetchPasskeyOptions())
    const cred = await navigator.credentials.get({ publicKey: opts, mediation: 'optional' })
    if (!cred) throw new Error('No passkey selected.')
    await submitAssertion(cred)
  } catch (e) {
    passkeyError.value = e.message || 'Could not sign in with passkey.'
  } finally {
    passkeyBusy.value = false
  }
}

async function startConditional() {
  if (typeof PublicKeyCredential === 'undefined') return
  if (!PublicKeyCredential.isConditionalMediationAvailable) return
  if (!await PublicKeyCredential.isConditionalMediationAvailable().catch(() => false)) return
  try {
    const opts = decodeAssertionOptions(await fetchPasskeyOptions())
    conditionalAbort = new AbortController()
    const cred = await navigator.credentials.get({
      publicKey: opts,
      mediation: 'conditional',
      signal:    conditionalAbort.signal,
    })
    if (cred) await submitAssertion(cred)
  } catch (e) {
    if (e?.name === 'AbortError') return
    // silent — conditional UI failures shouldn't disrupt the password form
  }
}

onMounted(() => { startConditional() })
onBeforeUnmount(() => { if (conditionalAbort) conditionalAbort.abort() })

async function submit() {
  if (conditionalAbort) { conditionalAbort.abort(); conditionalAbort = null }
  if (busy.value) return
  error.value = ''
  busy.value = true
  const { ok, data } = await post('/api/auth/login', {
    email:    email.value,
    password: password.value,
    remember: remember.value ? '1' : '',
    redirect: props.redirect,
    plan:     props.plan,
    next:     props.next,
  })
  if (ok && data.step === 'totp') {
    emit('step', 'totp')
    return
  }
  if (ok && data.redirect) {
    window.location.href = data.redirect
    return
  }
  error.value = data.error || 'Login failed.'
  busy.value = false
}
</script>

<template>
  <div class="auth-card hf-glass">
    <h1>Log In</h1>

    <div v-if="error" class="error" role="alert">{{ error }}</div>
    <div v-if="success" class="success">{{ success }}</div>

    <form novalidate @submit.prevent="submit">
      <div class="form-group">
        <label for="email">Email address</label>
        <input
          id="email"
          v-model="email"
          class="hf-input"
          type="email"
          name="email"
          autocomplete="username webauthn"
          required
          autofocus
        />
      </div>

      <div class="form-group">
        <label for="password">Password</label>
        <input
          id="password"
          v-model="password"
          class="hf-input"
          type="password"
          name="password"
          autocomplete="current-password"
          required
        />
      </div>

      <div class="form-group">
        <label class="checkbox-label">
          <input v-model="remember" type="checkbox" name="remember" value="1" />
          Remember me for 30 days
        </label>
      </div>

      <button type="submit" class="hf-btn primary auth-submit" :disabled="busy">
        {{ busy ? 'Logging in…' : 'Log In' }}
      </button>
    </form>

    <div class="auth-divider"><span>or</span></div>

    <button
      type="button"
      class="hf-btn primary auth-passkey-btn"
      :disabled="passkeyBusy"
      @click="startVisibleButton"
    >
      <span aria-hidden="true">🔑</span> {{ passkeyBusy ? 'Working…' : 'Sign in with passkey' }}
    </button>

    <p v-if="passkeyError" class="auth-error" role="alert">{{ passkeyError }}</p>

    <div class="auth-links">
      <a href="/forgot">Forgot your password?</a>
      <span class="auth-links__sep">·</span>
      <a href="/register">Create an account</a>
    </div>
  </div>
</template>
